import React, { useCallback, useMemo } from 'react';
import cn from 'bem-cn';

import { PromptStatus } from '@ai-platform/utils';
import noImage from '../../images/no_image.png';
import prompthubEllipse from '../../images/icons/prompthub-ellipse.svg';

import { RoutePaths } from '../../constants';
import { Button, Icon, Link, Video } from '../UI';
import { TagList } from '../TagList';
import { ScreenSize, useGetScreenSize, useLike, useVideoOnHover, MetrikaGoal, useMetrika } from '../../hooks';
import { sanitize } from '../../utils/sanitize';
import { openAliceChat } from '../../utils/alice/alice';
import { mapCategoriesToRussian } from '../../utils/categories';

import { useParallax } from './useParallax';
import { Like } from './components/Like';
import { Status } from './components/Status';
import { PromptCardProps } from './PromptCard.types';
import './PromptCard.scss';

const b = cn('prompt-card');

const scaleX = 340 / 308;
const scaleY = 400 / 368;

export const PromptCard: React.FC<PromptCardProps> = ({
    prompt,
    size = 'normal',
    className,
    withHover,
    showStatus = false,
}) => {
    const screenSize = useGetScreenSize();

    const {
        title,
        description,
        videoData: { url: videoUrl },
        imageData: { urlOptimized: imageUrl },
        categories,
    } = prompt;
    const sanitaziedTitle = sanitize(title);
    const sanitaziedDescription = sanitize(description);
    const authorImage = prompt.author?.image || prompthubEllipse;
    const tags = mapCategoriesToRussian(categories.slice(0, 3));

    const isPhotoRevival = categories?.includes('photo-revival');
    const isFullImageCard = prompt.author?.uid === '0' || isPhotoRevival;
    const needHover = withHover && screenSize !== ScreenSize.Mobile && screenSize !== ScreenSize.Tablet;

    const mainColor: string = prompt.imageData?.colors?.MainColor;

    const { likes, isLiked, toggleLike } = useLike({
        promptId: Number(prompt.id),
        initialLikes: prompt.likes || 0,
        initialIsLiked: prompt.isLiked || false,
    });

    const { panelRef, subpanelRef, transformX, transformY, isHovered } = useParallax(1);

    // Хук для управления видео при ховере (только для photo-revival карточек)
    const { videoRef, onMouseEnter: onVideoMouseEnter, onMouseLeave: onVideoMouseLeave } = useVideoOnHover();
    const { reachGoal } = useMetrika();

    const handleOpenAliceChat = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            openAliceChat(prompt?.id);
            reachGoal(MetrikaGoal.ClickoutChat);
        },
        [prompt, reachGoal],
    );

    const hover = `${isHovered ? `scale(${scaleX}, ${scaleY})` : ''}`;
    const parallaxStyle = {
        transform: `perspective(200px) rotateY(${transformX}deg) rotateX(${transformY}deg)`,
    };

    // Для editorial карточек масштабирование применяется к background
    // Для обычных карточек - ко всей карточке
    const cardParallaxStyle = needHover ? parallaxStyle : {};

    const backgroundHoverStyle = needHover
        ? ({
            transform: `${hover}`,
        } as React.CSSProperties)
        : {};

    const bgImage = isFullImageCard ? `url(${imageUrl})` : '';
    // Scale применяется к background только для editorial карточек
    const backgroundStyle = {
        '--bg-image': bgImage,
        '--image-main-color': isFullImageCard ? mainColor : '',
        ...backgroundHoverStyle,
    };

    const getTagLimit = useCallback(() => {
        if (screenSize === ScreenSize.Mobile || size === 'small') {
            return 1;
        }

        return 2;
    }, [screenSize, size]);

    const promptLink = useMemo(() => {
        if (
            prompt.status === PromptStatus.Moderation ||
            prompt.status === PromptStatus.Rejected ||
            prompt.status === PromptStatus.Postponed
        ) {
            return `${RoutePaths.NewPromptStatus}/${prompt.id}`;
        }

        return `${RoutePaths.Prompt}/${prompt.id}`;
    }, [prompt.id, prompt.status]);

    const renderProfile = () => {
        if (authorImage) {
            return <img src={authorImage} alt={'Аватарка'} className={b('avatar')} />;
        }

        return <div />;
    };

    return (
        <Link
            to={promptLink}
            state={{ fromClient: true }}
            ref={panelRef}
            className={b(
                { size, 'full-image': isFullImageCard, 'with-hover': needHover, 'photo-revival': isPhotoRevival },
                [className],
            )}
            style={cardParallaxStyle}
            onMouseEnter={isPhotoRevival ? onVideoMouseEnter : undefined}
            onMouseLeave={isPhotoRevival ? onVideoMouseLeave : undefined}
        >
            <div className={b('background')} ref={subpanelRef} style={backgroundStyle}>
                {isPhotoRevival && Boolean(videoUrl) && (
                    <Video
                        ref={videoRef}
                        className={b('video')}
                        src={videoUrl}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                    />
                )}
            </div>
            <div className={b('content')}>
                <div className={b('header')}>
                    {showStatus && prompt.status ? <Status status={prompt.status} /> : renderProfile()}
                    {(!showStatus || prompt.status === PromptStatus.Published) && (
                        <Like
                            size={size}
                            count={likes}
                            isEditorial={isFullImageCard}
                            isLiked={isLiked}
                            onClick={toggleLike}
                        />
                    )}
                </div>
                <div className={b('body')}>
                    <div className={b('front-side')}>
                        {!isFullImageCard && (
                            <div className={b('image-block')}>
                                <img src={imageUrl || noImage} alt={prompt.title} className={b('image')} />
                            </div>
                        )}
                        <div className={b('title')}>
                            <p dangerouslySetInnerHTML={{ __html: sanitaziedTitle }} />
                        </div>
                        <TagList maxVisible={getTagLimit()} tags={tags} />
                    </div>
                    <div className={b('back-side')}>
                        <div className={b('alter-text')}>
                            <p dangerouslySetInnerHTML={{ __html: sanitaziedDescription }} />
                        </div>
                        <div className={b('button-group')}>
                            <Button variant="secondary" fullWidth={!isPhotoRevival} className={b('alter-button')}>
                                Смотреть
                            </Button>
                            {!isPhotoRevival && prompt.status === PromptStatus.Published && (
                                <Button fullWidth className={b('alter-button')} onClick={handleOpenAliceChat}>
                                    <Icon name="alice" size={20} />В чат
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Link>
    );
};
